import React, { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import * as api from '../../api';
import { Btn, Modal, RNLLoader, Select } from '../UI';
import { Panel } from '../materialTakeoff/MTOShared';
import { asOptions } from '../materialMapping/mmUtils';

const th = { padding: '10px 12px', fontSize: 10, fontWeight: 800, color: '#fff', background: '#0b318bff', border: '1px solid black',
             textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1 };
const td = { padding: '8px 12px', fontSize: 12.5, borderBottom: '1px solid var(--border-light)', verticalAlign: 'top' };

// Give current BOQ lines codes in this format: preview first, then apply. A line's old code stays on the register.
export default function CGApply({ projectId, disciplines, onChanged, canEdit, toast }) {
  const [boqs, setBoqs] = useState([]);
  const [lists, setLists] = useState({ divisions: [], categories: [] });
  const [f, setF] = useState({ boq: '', division: '', category: '', discipline: '', mode: 'uncoded' });
  const [plan, setPlan] = useState(null);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    Promise.all([api.listProjectBOQs(projectId).catch(() => []), api.listProjectDivisions(projectId).catch(() => []),
                 api.listDmrCategories(projectId).catch(() => [])])
      .then(([b, d, c]) => {
        setBoqs(Array.isArray(b) ? b : (b?.data || []));
        setLists({ divisions: Array.isArray(d) ? d : (d?.data || []), categories: Array.isArray(c) ? c : (c?.data || []) });
      });
  }, [projectId]);

  const body = (ids) => ({
    project_id: projectId, boq_id: f.boq || null, project_division_id: f.division || null, dmr_category_id: f.category || null,
    discipline_code: f.discipline || null, only_uncoded: f.mode === 'uncoded', ...(ids ? { boq_item_ids: ids } : {}),
  });

  const preview = async () => {
    setBusy('preview');
    try {
      const r = await api.previewApplyCodes(body());
      setPlan(r.lines);
      setPicked(Object.fromEntries(r.lines.filter(l => l.proposed_code).map(l => [l.boq_item_id, true])));
    } catch (e) {
      toast?.show?.(e.message || 'Preview failed', 'error');
    }
    setBusy('');
  };
  useEffect(() => { setPlan(null); }, [f]);

  const ids = Object.keys(picked).filter(k => picked[k]);
  const apply = async () => {
    setBusy('apply');
    try {
      const r = await api.applyBoqCodes(body(ids));
      api.clearApiCache();
      toast?.show?.(`${r.applied} BOQ line(s) re-coded`, 'success');
      setPlan(r.lines.filter(l => ids.includes(l.boq_item_id)));
      setPicked({});
      onChanged();
    } catch (e) {
      toast?.show?.(e.message || 'Could not apply codes', 'error');
    }
    setBusy('');
    setConfirm(false);
  };

  if (!canEdit) return <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Only project managers, quantity surveyors and administrators can re-code BOQ lines.</div>;
  const unlinked = (plan || []).filter(l => !l.discipline_code).length;

  return (
    <div>
      <Panel title="Which BOQ lines">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <Select label="BOQ" value={f.boq} onChange={v => setF(p => ({ ...p, boq: v }))}
                  options={[{ value: '', label: 'All current BOQs' }, ...boqs.map(b => ({ value: b.id || b.boq_id, label: b.name }))]} />
          <Select label="Building" value={f.division} onChange={v => setF(p => ({ ...p, division: v }))} options={asOptions(lists.divisions, 'project_division_id', 'All buildings')} />
          <Select label="BOQ Discipline (category)" value={f.category} onChange={v => setF(p => ({ ...p, category: v }))} options={asOptions(lists.categories, 'id', 'All')} />
          <Select label="Lines" value={f.mode} onChange={v => setF(p => ({ ...p, mode: v }))}
                  options={[{ value: 'uncoded', label: 'Only lines without a register code' }, { value: 'all', label: 'All lines (re-code everything)' }]} />
          <Select label="Code discipline for unlinked categories" value={f.discipline} onChange={v => setF(p => ({ ...p, discipline: v }))}
                  options={[{ value: '', label: '— none —' }, ...disciplines.filter(d => d.is_active).map(d => ({ value: d.code, label: `${d.code} — ${d.name}` }))]} />
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '10px 0 12px' }}>
          A line's code discipline comes from its BOQ discipline (category) as linked in Settings. Lines whose category is not linked use the
          discipline chosen above, or are left out.
        </p>
        <Btn onClick={preview} loading={busy === 'preview'}><Eye size={14} /> Preview Codes</Btn>
      </Panel>

      {plan && (
        <Panel title={`Preview — ${plan.length} line(s)`}
               right={<Btn disabled={!ids.length} onClick={() => setConfirm(true)}>Apply to {ids.length} line(s)</Btn>}>
          {unlinked > 0 && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: '#fef9c3', fontSize: 12.5, marginBottom: 10 }}>
              {unlinked} line(s) have no code discipline — link their category in Settings or choose one above, then preview again.
            </div>
          )}
          {plan.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Nothing to code — every matching line already has a register code.</div> : (
            <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 10, maxHeight: 520, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={{ ...th, width: 30 }}>
                    <input type="checkbox" checked={ids.length > 0 && ids.length === plan.filter(l => l.proposed_code).length}
                           onChange={e => setPicked(Object.fromEntries(plan.filter(l => l.proposed_code).map(l => [l.boq_item_id, e.target.checked])))} />
                  </th>
                  {['Building', 'BOQ', 'Current Code', 'Description', 'Discipline', 'New Code'].map(h => <th key={h} style={th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {plan.map(l => (
                    <tr key={l.boq_item_id}>
                      <td style={td}>{l.proposed_code && <input type="checkbox" checked={!!picked[l.boq_item_id]} onChange={e => setPicked(p => ({ ...p, [l.boq_item_id]: e.target.checked }))} />}</td>
                      <td style={td}>{l.building || '—'}</td>
                      <td style={td}>{l.boq_name || '—'}</td>
                      <td style={{ ...td, fontFamily: 'monospace' }}>{l.current_code}</td>
                      <td style={{ ...td, minWidth: 240, maxWidth: 420 }}>{l.description}</td>
                      <td style={td}>{l.discipline || '—'}{l.discipline_code && <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{l.discipline_code}</div>}</td>
                      <td style={{ ...td, fontFamily: 'monospace', fontWeight: 800, color: l.proposed_code ? '#0b318b' : '#b91c1c' }}>
                        {l.proposed_code || <span style={{ fontFamily: 'var(--font)', fontWeight: 600, fontSize: 12 }}>{l.issue}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {confirm && (
        <Modal title="Apply new codes" onClose={() => setConfirm(false)} width={520} blur={2}>
          <p style={{ fontSize: 13.5, lineHeight: 1.5, margin: 0 }}>
            {ids.length} BOQ line(s) get new codes now. Their current codes are kept on the register as “previous code”; a line's earlier register
            code is voided. Numbers are taken at this moment, so they can differ from the preview if someone issued codes meanwhile.
          </p>
          {busy === 'apply' && <RNLLoader title="Applying codes..." />}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <Btn variant="secondary" onClick={() => setConfirm(false)}>Cancel</Btn>
            <Btn onClick={apply} loading={busy === 'apply'}>Apply Codes</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
